import { motion, AnimatePresence } from "framer-motion";
import { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { Button } from "@/components/ui/button";
import TaskInput from "./TaskInput";
import TaskItem from "./TaskItem";
import { PlusIcon, GripVerticalIcon, CalendarDaysIcon, ArrowDownAZIcon, Trash2Icon, MoreVertical } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useDeleteTasklist, useInsertTask, useUpdateTask, useTaskEventMappings, useUpdateCalendarEvent, useCompletedTaskList } from "@/hooks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useUIStore from "@/stores/useUIStore";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileActionSheet, ActionSheetItem, ActionSheetSeparator } from "@/components/MobileActionSheet";
import { CheckIcon } from "lucide-react";

import { ExtendedTask, TaskEditMode, TaskEditState, TaskSortType } from "./types";
import { tasks_v1 } from "@googleapis/tasks";
import { makeDroppableId, makeDraggableId } from "@/helpers";
import { useSortedTaskList } from "@/hooks/useSortedTaskList";
import { useTaskSortStore } from "@/stores/useTaskSortStore";
import { TaskListSkeleton } from "@/components/TaskListSkeleton";

// Define the TaskFilter type
type TaskFilter = {
  id: string;
  name: string;
  filter: (task: ExtendedTask) => boolean;
};

// Types
interface TaskListProps {
  account: any;
  list: {
    id: string;
    title: string;
  };
  dragHandleProps?: any;
}

// Create filter generator functions
const createSearchFilter = (searchTerm: string): TaskFilter => {
  const trimmed = searchTerm.trim();
  return {
    id: 'search',
    name: 'Search',
    filter: (task: ExtendedTask) => 
      !trimmed || !task.title 
        ? true 
        : task.title.toLowerCase().includes(trimmed.toLowerCase())
  };
};

const createUpcomingFilter = (enabled: boolean): TaskFilter => ({
  id: 'upcoming',
  name: 'Upcoming Tasks',
  filter: (task: ExtendedTask) => 
    !enabled
      ? true
      : task.due
        ? new Date(task.due).getTime() <= new Date().getTime()
        : true
});

const createPastFilter = (enabled: boolean): TaskFilter => ({
  id: 'past',
  name: 'Past Tasks',
  filter: (task: ExtendedTask) => 
    !enabled
      ? true
      : task.due
        ? new Date(task.due).getTime() > new Date().getTime()
        : true
});

const createNeedsDateFilter = (enabled: boolean): TaskFilter => ({
  id: 'needsDate',
  name: 'Needs Date',
  filter: (task: ExtendedTask) => 
    !enabled
      ? true
      : !!task.due
});

// Filter application function
const applyFilters = (tasks: ExtendedTask[], filters: TaskFilter[]): ExtendedTask[] => {
  return tasks.filter(task => 
    filters.every(filter => filter.filter(task))
  );
};

const TaskList = ({ 
  account, 
  list,
  dragHandleProps,
}: TaskListProps) => {
  const deleteTasklistMutation = useDeleteTasklist(account);
  const insertTaskMutation = useInsertTask(account, list.id);
  const updateTaskMutation = useUpdateTask(account, list.id);
  const updateCalendarEventMutation = useUpdateCalendarEvent();
  
  const { data: taskEventMappings } = useTaskEventMappings(account.id, account.id);
  
  const isMobile = useIsMobile();
  const [listMenuOpen, setListMenuOpen] = useState(false);
  const [editState, setEditState] = useState<TaskEditState>({ mode: TaskEditMode.None });
  const [inputResetKey, setInputResetKey] = useState(0);
  
  // Use the sort type from the store instead of local state
  const { 
    getTasklistSortType, 
    setTasklistSortType, 
    sortTasksBySortType 
  } = useTaskSortStore();

  const needsDate = useUIStore((state) => state.filters.needsDate);
  const search = useUIStore((state) => state.filters.search);
  const upcomingFilter = useUIStore((state) => state.filters.upcoming);
  const pastFilter = useUIStore((state) => state.filters.past);
  
  const sortType = getTasklistSortType(account.id, list.id);
  const { tasks: listItems, isPending } = useSortedTaskList(account, list.id, sortType);
  const {
    data: completedData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching: isCompletedFetching,
    isLoading: isCompletedLoading,
    isError: isCompletedError,
  } = useCompletedTaskList(account, list.id);

  const listOrder = useUIStore((state) => state.listOrder);
  const setListOrder = useUIStore((state) => state.setListOrder);

  const handleDeleteList = useCallback(() => {
    if (window.confirm("Are you sure you want to delete this list?")) {
      deleteTasklistMutation.mutate(
        { account, tasklistId: list.id },
        {
          onSuccess: () => {
            if (listOrder[account.id]) {
              const updatedOrder = listOrder[account.id].filter(id => id !== list.id);
              setListOrder(account.id, updatedOrder);
            }
          }
        }
      );
    }
  }, [deleteTasklistMutation, account, list.id, listOrder, setListOrder]);

  const handleChangeSortType = useCallback((value: string) => {
    const newSortType = value as TaskSortType;
    // Update the sort type in the store
    setTasklistSortType(account.id, list.id, newSortType);
    sortTasksBySortType(account.id, list.id, newSortType);
  }, [account.id, list.id, setTasklistSortType, sortTasksBySortType]);
  
  const handleAddTask = useCallback(() => {
    setEditState({ mode: TaskEditMode.New });
    setInputResetKey(0);
  }, []);

  const handleEditTask = useCallback((taskId: string) => {
    setEditState({ mode: TaskEditMode.Existing, taskId });
  }, []);

  const handleCloseTaskInput = useCallback(() => {
    setEditState({ mode: TaskEditMode.None });
    setInputResetKey(0);
  }, []);

  const handleSubmitTask = useCallback((taskData: Partial<ExtendedTask>, submissionMethod?: 'enter' | 'clickOutside' | 'datePicker') => {
    if (editState.mode === TaskEditMode.New) {
      // Insert new task
      insertTaskMutation.mutate({ 
        tasklist: list.id, 
        task: taskData as Partial<tasks_v1.Schema$Task> 
      });
      
      // If submitted via Enter key, keep input open and reset its state
      if (submissionMethod === 'enter') {
        setInputResetKey(prev => prev + 1);
        // Keep editState.mode as TaskEditMode.New to keep input open
      } else {
        setEditState({ mode: TaskEditMode.None });
      }
    } else if (editState.mode === TaskEditMode.Existing && editState.taskId) {
      // Update existing task
      updateTaskMutation.mutate({ 
        task: editState.taskId, 
        updatedFields: {
          ...taskData,
        }
      });
      
      // Sync to calendar if task has a linked event
      const mapping = taskEventMappings?.[editState.taskId];
      if (mapping) {
        updateCalendarEventMutation.mutate({
          accountId: account.id,
          calendarId: mapping.calendarId,
          eventId: mapping.eventId,
          task: taskData,
          userId: account.id,
        });
      }
      
      // Always close when editing existing tasks
      setEditState({ mode: TaskEditMode.None });
    }
  }, [editState, insertTaskMutation, updateTaskMutation, list.id, taskEventMappings, updateCalendarEventMutation, account.id]);

  // Create the filters based on the current ui state
  const filters = useMemo(() => [
    createSearchFilter(search),
    createUpcomingFilter(upcomingFilter),
    createPastFilter(pastFilter),
    createNeedsDateFilter(!!needsDate),
  ], [search, upcomingFilter, pastFilter, needsDate]);

  const filteredItems = useMemo(() => applyFilters(listItems || [], filters), [listItems, filters]);

  const completedTasksRaw = useMemo(
    () => completedData?.pages?.flatMap((page) => page.items) ?? [],
    [completedData]
  );
  const completedList = useMemo(() => applyFilters(completedTasksRaw, filters), [completedTasksRaw, filters]);

  if (isPending) return <TaskListSkeleton />;

  return (
    <Card className="border-0 bg-card rounded-[0.5rem]">
      {dragHandleProps && (
        <div
          {...dragHandleProps}
          className="flex justify-center items-center pt-1.5 pb-0.5 cursor-grab active:cursor-grabbing rounded-t-lg"
        >
          <div className="w-8 h-1 rounded-full bg-muted-foreground/25 hover:bg-muted-foreground/40 transition-colors" />
        </div>
      )}
      <CardHeader className="py-0 pt-2 pr-2 font-medium">
        <div className="flex justify-between items-center">
          <h2 className="text-muted-foreground text-sm inline ph-no-capture">{list.title}</h2>
          {isMobile ? (
            <>
              <Button
                className="rounded-full dark:hover:bg-muted"
                size="icon"
                variant="ghost"
                aria-label="List options"
                onClick={() => setListMenuOpen(true)}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
              <MobileActionSheet open={listMenuOpen} onOpenChange={setListMenuOpen} title={`Sort "${list.title}" by`}>
                <ActionSheetItem
                  icon={GripVerticalIcon}
                  label="Default"
                  selected={sortType === "position"}
                  trailing={sortType === "position" ? <CheckIcon className="h-4 w-4" /> : undefined}
                  onClick={() => { handleChangeSortType("position"); setListMenuOpen(false); }}
                />
                <ActionSheetItem
                  icon={CalendarDaysIcon}
                  label="Due Date"
                  selected={sortType === "dueDate"}
                  trailing={sortType === "dueDate" ? <CheckIcon className="h-4 w-4" /> : undefined}
                  onClick={() => { handleChangeSortType("dueDate"); setListMenuOpen(false); }}
                />
                <ActionSheetItem
                  icon={ArrowDownAZIcon}
                  label="Alphabetical"
                  selected={sortType === "alphabetical"}
                  trailing={sortType === "alphabetical" ? <CheckIcon className="h-4 w-4" /> : undefined}
                  onClick={() => { handleChangeSortType("alphabetical"); setListMenuOpen(false); }}
                />
                <ActionSheetSeparator />
                <ActionSheetItem
                  icon={Trash2Icon}
                  label="Delete List"
                  destructive
                  onClick={() => { setListMenuOpen(false); handleDeleteList(); }}
                />
              </MobileActionSheet>
            </>
          ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-full dark:hover:bg-muted" size="icon" variant="ghost">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" className="w-52 dark:bg-background p-1.5">
              <DropdownMenuLabel className="px-2 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground/70 font-medium">
                Sort by
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup 
                value={sortType} 
                onValueChange={handleChangeSortType}
              >
                <DropdownMenuRadioItem value="position" className="gap-2">
                  <GripVerticalIcon className="h-3.5 w-3.5 shrink-0" />
                  Default
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dueDate" className="gap-2">
                  <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0" />
                  Due Date
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="alphabetical" className="gap-2">
                  <ArrowDownAZIcon className="h-3.5 w-3.5 shrink-0" />
                  Alphabetical
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 text-red-400 focus:text-red-400 focus:bg-red-500/10 rounded-md"
                disabled={deleteTasklistMutation.isPending}
                onClick={handleDeleteList}
              >
                <Trash2Icon className="h-3.5 w-3.5 shrink-0" />
                Delete List
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </CardHeader>

      <CardContent className="pb-0 px-0">
        {(
          <>
            {/* Add task button */}
            <motion.button
              className="group relative w-full flex items-center gap-3 pl-4 pr-3 py-2 rounded-md text-sm transition-colors duration-200 hover:bg-accent/50"
              onClick={handleAddTask}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 group-hover:h-5 bg-blue-500 rounded-full transition-all duration-200" />

              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 group-hover:shadow-[0_0_8px_rgba(59,130,246,0.3)] transition-all duration-200">
                <div className="group-hover:rotate-90 transition-transform duration-300">
                  <PlusIcon strokeWidth={2.5} className="h-3 w-3 text-blue-500" />
                </div>
              </div>

              <span className="text-muted-foreground group-hover:text-blue-500 transition-colors duration-200">
                Add task
              </span>
            </motion.button>

            {/* Pending tasks */}
            <Droppable droppableId={makeDroppableId(account.id, list.id)} type="pending">
              {(provided, snapshot) => (
                <ul
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`overflow-y-auto max-h-[350px] min-h-[5px] scrollbar-thin scrollbar-thumb-muted-foreground scrollbar-track-card transition-colors ${
                    snapshot.isDraggingOver ? 'bg-muted/50' : ''
                  }`}
                >
                  {/* New task input */}
                  <AnimatePresence>
                    {editState.mode === TaskEditMode.New && (
                      <motion.li 
                        key={`new-task-${inputResetKey}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <TaskInput
                          key={`task-input-${inputResetKey}`}
                          listId={list.id}
                          account={account}
                          onClose={handleCloseTaskInput}
                          onSubmit={handleSubmitTask}
                        />
                      </motion.li>
                    )}
                  </AnimatePresence>

                  {/* Render pending tasks */}
                  <AnimatePresence>
                    {filteredItems.map((task, index) => {
                      if (!task.id) return null;
                      const isEditing = editState.mode === TaskEditMode.Existing && editState.taskId === task.id;

                      return (
                        <Draggable 
                          key={task.id} 
                          draggableId={makeDraggableId(account.id, list.id, task.id)} 
                          index={index}
                          isDragDisabled={isEditing}
                        >
                          {(provided) => (
                            <motion.li 
                              {...(provided.draggableProps as any)} 
                              {...(provided.dragHandleProps as any)} 
                              ref={provided.innerRef}
                              initial={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                              className={`z-10 flex flex-col border border-transparent overflow-hidden ${!isEditing ? "hover:bg-muted dark:hover:border-t dark:hover:shadow dark:hover:bg-muted dark:active:bg-muted" : ""}`}
                            >
                              <AnimatePresence mode="wait" initial={false}>
                                {isEditing && (
                                  <motion.div
                                    key={`${task.id}-edit`}
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                  >
                                    <TaskInput
                                      listId={list.id}
                                      account={account}
                                      task={task}
                                      onClose={handleCloseTaskInput}
                                      onSubmit={handleSubmitTask}
                                    />
                                  </motion.div>
                                )}
                              </AnimatePresence>
                              {!isEditing && (
                                <TaskItem
                                  task={task}
                                  list={list.id}
                                  account={account}
                                  variant="needsAction"
                                  onEdit={handleEditTask}
                                />
                              )}
                            </motion.li>
                          )}
                        </Draggable>
                      );
                    })}
                  </AnimatePresence>

                  {/* Empty state */}
                  {filteredItems.length === 0 && editState.mode !== TaskEditMode.New && (
                    <li className="flex flex-col items-center justify-center py-4 select-none">
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-muted-foreground/20 mb-2"
                      >
                        <rect x="8" y="4" width="32" height="40" rx="4" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M16 16H32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M16 24H28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M16 32H24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      <p className="text-[11px] text-muted-foreground/30 font-medium">No tasks yet</p>
                    </li>
                  )}

                  {provided.placeholder}
                </ul>
              )}
            </Droppable>

            {/* Completed tasks - only show when there are completed tasks */}
            {completedList.length > 0 && (
              <Accordion type="single" collapsible>
                <AccordionItem value="completed" className="border-none">
                  <AccordionTrigger className="px-4 text-sm border-0 shadow-none ring-0 focus-visible:ring-0 data-[state=open]:bg-muted/70 data-[state=open]:dark:bg-background/70">
                    {isCompletedLoading
                      ? "Completed (loading...)"
                      : isCompletedError
                        ? "Completed (error)"
                        : `Completed (${completedList.length}${hasNextPage ? "+" : ""})`}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="overflow-y-auto max-h-[300px] scrollbar-thin scrollbar-thumb-muted-foreground scrollbar-track-card">
                      <AnimatePresence>
                        {completedList.map((task) => (
                          <motion.li
                            key={task.id}
                            initial={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            className="flex flex-col border border-transparent overflow-hidden hover:bg-muted dark:hover:shadow dark:hover:bg-muted dark:active:bg-muted"
                          >
                            <TaskItem
                              key={task.id}
                              task={task}
                              list={list.id}
                              account={account}
                              variant="completed"
                              onEdit={handleEditTask}
                            />
                          </motion.li>
                        ))}
                      </AnimatePresence>
                      {hasNextPage && (
                        <li>
                          <Button
                            variant="link"
                            size="sm"
                            className="w-full hover:no-underline text-blue-500 hover:text-blue-600"
                            onClick={() => fetchNextPage()}
                            disabled={isFetchingNextPage || isCompletedFetching}
                          >
                            {isFetchingNextPage ? "Loading..." : "Load more"}
                          </Button>
                        </li>
                      )}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TaskList; 